'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';

import { useRouter } from 'next/navigation';

export type { ContactFormValues } from './contact-form-runtime';
type Runtime = typeof import('./contact-form-runtime');
let runtimePromise: Promise<Runtime> | undefined;
export function loadContactRuntime() {
  return runtimePromise ??= import('./contact-form-runtime').catch(error => {
    runtimePromise = undefined;
    throw error;
  });
}

export function ContactForm() {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(state => state.currentLocale);
  const router = useRouter();

  const formRef = useRef<HTMLFormElement>(null);
  const submitting = useRef(false);
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const warm = () => { void loadContactRuntime().catch(() => {}); };

  useEffect(() => {
    const form = formRef.current;
    if (!form || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { warm(); observer.disconnect(); }
    }, { rootMargin: '200px 0px' });
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  function showErrors(next: Record<string, string>) {
    setErrors(next);
    const field = Object.keys(next)[0];
    const input = formRef.current?.elements.namedItem(field) as HTMLElement | null;
    input?.focus?.();
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setErrors({});
    // Snapshot actual inputs before awaiting anything; the input DOM is never replaced.
    const values = new FormData(event.currentTarget);
    let runtime: Runtime | undefined;
    try {
      runtime = await loadContactRuntime();
      const parsed = runtime.contactSchema(formValidationMessages).safeParse(runtime.readContactValues(values));
      if (!parsed.success) {
        showErrors(Object.fromEntries(parsed.error.issues.map(issue => [issue.path.join('.'), issue.message])));
        return;
      }
      await runtime.sendContactMessage({ locale, data: parsed.data });
      runtime.toast({ title: t('text658'), variant: 'success' });
      router.push(ROUTES.THANK_YOU());
    } catch (error) {
      if (runtime && error instanceof runtime.ValidationError) {
        showErrors(Object.fromEntries(Object.entries(error.fields).map(([field, messages]) => [field, messages.join('\n')])));
      } else {
        setErrors({ form: t('text657') });
        runtime?.toast({ title: t('text657'), description: (error as Error)?.message, variant: 'destructive' });
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  const fields = [
    { name: 'name', label: t('text33'), autoComplete: 'given-name', required: true },
    { name: 'surname', label: t('text34'), autoComplete: 'family-name', required: true },
    { name: 'phone', label: t('text36'), autoComplete: 'tel', required: true },
    { name: 'email', label: t('text35'), autoComplete: 'email', required: true, type: 'email' },
    { name: 'custom.interest', label: t('text2339'), autoComplete: 'off', required: false },
  ];
  const errorNode = (field: string) => errors[field]
    ? <p id={id + '-' + field + '-error'} role="alert" className="text-sm font-medium text-destructive">{errors[field]}</p>
    : null;

  return (
    <form ref={formRef} noValidate onSubmit={submit} onFocusCapture={warm} onPointerEnter={warm}
      aria-busy={busy} className="ea-contact-form flex w-full flex-col items-start justify-start gap-6">
      <div className="ea-contact-form__content-wrapper grid w-full grid-cols-12 gap-4">
        {fields.map(field => (
          <div key={field.name} className={field.name === 'custom.interest' ? 'col-span-12 w-full' : 'col-span-12 w-full sm:col-span-6'}>
            <div className="space-y-2 p-[1px]">
              <label className="sr-only" htmlFor={id + '-' + field.name}>{field.label}</label>
              <Input id={id + '-' + field.name} name={field.name} type={field.type}
                autoComplete={field.autoComplete} required={field.required} placeholder={field.label}
                aria-invalid={!!errors[field.name]} aria-describedby={errors[field.name] ? id + '-' + field.name + '-error' : undefined} />
              {errorNode(field.name)}
            </div>
          </div>
        ))}
        <div className="ea-contact-form__field ea-contact-form__text-field col-span-12 w-full space-y-2">
          <label className="sr-only" htmlFor={id + '-text'}>{t('text2340')}</label>
          <div className="ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper flex w-full flex-row items-center space-x-2">
            <Textarea id={id + '-text'} name="text" placeholder={t('text2340')} rows={5}
              aria-invalid={!!errors.text} aria-describedby={errors.text ? id + '-text-error' : undefined} />
          </div>
          {errorNode('text')}
        </div>
      </div>
      <div className="col-span-12">{errorNode('g-recaptcha-response')}{errorNode('form')}{errorNode('gdpr')}</div>
      <div className="col-span-12 flex w-full flex-wrap items-start justify-between gap-4">
        <p className="w-full max-w-[360px] text-sm font-light text-black">
          {t('text2341')}{' '}
          <Link prefetch={false} href={getDefaultUrlForTosOrGdprLocalized(ROUTES.GDPR(), locale, 'gdpr')} className="font-semibold text-primary">{t('text2342')}</Link>
        </p>
        <Button type="submit" className="ea-contact-form__submit w-fit whitespace-nowrap" isLoading={busy}>{t('text2343')}</Button>
      </div>
    </form>
  );
}
